import { useState, useEffect } from 'react';
import { useFriends } from '../api/hooks';
import { api } from '../api/client';
import { Link } from 'react-router-dom';
import { Stack, Row, Text, Avatar, Badge, Panel, List, ListItem, Button, Tag, EmptyState, Money } from '../ui';
import { MessageDrawer } from './MessageDrawer';
import { ProposeMeetupModal } from './ProposeMeetupModal';
import { VoiceNoteRecorder } from './VoiceNoteRecorder';

export function People() {
  const { data: friends, loading } = useFriends();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [messageTarget, setMessageTarget] = useState<any>(null);
  const [meetupTarget, setMeetupTarget] = useState<any>(null);
  const [voiceTarget, setVoiceTarget] = useState<any>(null);
  const f = friends.find((x: any) => x.id === selectedId) ?? friends[0];
  const [sharedDeals, setSharedDeals] = useState<any[]>([]);
  const [sharedLoading, setSharedLoading] = useState(false);

  useEffect(() => {
    if (!f) return;
    let cancelled = false;
    setSharedLoading(true);
    api.sharedDeals(f.id).then((res: any) => {
      if (!cancelled) { setSharedDeals(res.deals ?? []); setSharedLoading(false); }
    }).catch(() => { if (!cancelled) { setSharedDeals([]); setSharedLoading(false); } });
    return () => { cancelled = true; };
  }, [f?.id]);

  if (loading) return <Text muted variant="small">loading…</Text>;

  if (friends.length === 0) {
    return <Stack gap={3}>
      <Text variant="display">People</Text>
      <EmptyState title="no friends yet" hint="invite friends to your inner circle" />
    </Stack>;
  }

  return (
    <>
    <div className="grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-4">
      {/* Friend list sidebar */}
      <aside className="md:col-span-3">
        <Panel title={`${friends.length} friend${friends.length === 1 ? '' : 's'}`}>
          <List>
            {friends.map((x: any) => (
              <ListItem key={x.id} active={x.id === f?.id} onClick={() => setSelectedId(x.id)}>
                <Row gap={2} align="center">
                  <Avatar name={x.name} friendId={x.id} hasAvatar={!!x.avatarUrl} size="md" />
                  <Stack gap={0.5} className="flex-1 min-w-0">
                    <Row gap={1} align="center">
                      <Text variant="small" className="truncate font-medium">{x.name}</Text>
                      {x.online && <span className="inline-block w-2 h-2 rounded-full bg-positive shrink-0" title="online now" />}
                    </Row>
                    <Text variant="label" className="truncate">{x.city ?? '—'}</Text>
                  </Stack>
                </Row>
              </ListItem>
            ))}
          </List>
        </Panel>
      </aside>

      <section className="md:col-span-9">
        {f && <Stack gap={3}>
          <div className="hairline rounded-sm p-3 sm:p-4">
            {/* Mobile: stacked layout */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-3 sm:items-start">
              <div className="flex items-center gap-3 sm:block">
                <Avatar name={f.name} friendId={f.id} hasAvatar={!!f.avatarUrl} size="lg" />
                <div className="sm:hidden">
                  <Text variant="display">{f.name}</Text>
                  {f.online && <Badge tone="positive">online</Badge>}
                </div>
              </div>
              <Stack gap={1} className="flex-1 min-w-0">
                <Text variant="display" className="hidden sm:block">{f.name}</Text>
                <Row gap={2} wrap>
                  <Text muted variant="small">{f.city ?? '—'}{f.timezone ? ` · ${f.timezone}` : ''}</Text>
                  <span className="hidden sm:inline">{f.online && <Badge tone="positive">online</Badge>}</span>
                </Row>
                {f.sectors && f.sectors.length > 0 && <Row gap={1} wrap>{f.sectors.map((s: string) => <Tag key={s}>{s}</Tag>)}</Row>}
              </Stack>
            </div>
            {/* Action buttons: full-width on mobile, inline on desktop */}
            <div className="flex flex-col sm:flex-row gap-2 mt-3">
              <Button variant="primary" size="sm" className="flex-1 sm:flex-none justify-center" onClick={() => setMessageTarget(f)}>message</Button>
              <Button variant="secondary" size="sm" className="flex-1 sm:flex-none justify-center" onClick={() => setMeetupTarget(f)}>propose meetup</Button>
              <Button variant="secondary" size="sm" className="flex-1 sm:flex-none justify-center" onClick={() => setVoiceTarget(f)}>voice note</Button>
            </div>
          </div>

          <Panel title={`Shared deals${sharedDeals.length > 0 ? ` (${sharedDeals.length})` : ''}`}>
            {sharedLoading ? <Text muted variant="small">loading…</Text> :
              sharedDeals.length === 0 ?
                <EmptyState title="no shared deals yet" hint={`once you and ${f.name} both touch a deal it appears here`} /> :
                <List>
                  {sharedDeals.map((d: any) => (
                    <ListItem key={d.id}>
                      <Link to={`/deals/${d.id}`} className="block">
                        <Row justify="between">
                          <Stack gap={0.5}>
                            <Text variant="small" className="font-medium">{d.name}</Text>
                            <Row gap={1} wrap>
                              {d.stage && <Tag>{d.stage}</Tag>}
                              {d.sector && <Tag>{d.sector}</Tag>}
                              <Text variant="label">you: {d.myRole} · {f.name.split(' ')[0]}: {d.theirRole}</Text>
                            </Row>
                          </Stack>
                          {d.ticketSize > 0 && <Money amount={d.ticketSize} currency={d.currency || 'USD'} className="text-sm" />}
                        </Row>
                      </Link>
                    </ListItem>
                  ))}
                </List>
            }
          </Panel>

          <Panel title="Private notes">
            <Text variant="small" muted>Only you see this. Notes about {f.name} sync across your devices.</Text>
          </Panel>
        </Stack>}
      </section>
    </div>

    {/* Modals at page level */}
    {messageTarget && <MessageDrawer friend={messageTarget} onClose={() => setMessageTarget(null)} />}
    {meetupTarget && <ProposeMeetupModal friend={meetupTarget} onClose={() => setMeetupTarget(null)} />}
    {voiceTarget && <VoiceNoteRecorder friend={voiceTarget} onClose={() => setVoiceTarget(null)} onSent={() => { setVoiceTarget(null); setMessageTarget(voiceTarget); }} />}
    </>
  );
}
